import React, { useEffect, useState } from 'react';
import { 
  Brain, 
  Heart, 
  Zap,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Download,
  ArrowRight,
  Shield,
  Activity,
  Eye,
  Moon,
  Battery,
  Frown,
  Thermometer,
  Droplets,
  Clock,
  Sparkles,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { siteConfig } from '../data/mock';

const PDF_URL = 'https://customer-assets.emergentagent.com/job_20ddba76-5f33-4fc9-9b71-7f5fbfb3efdd/artifacts/vpf7bkq2_Cellular-Recovery-After-Substance-Use.pdf';

const OpportunityPage = () => {
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasShownPopup, setHasShownPopup] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (window.location.hash === '#book') {
      setTimeout(() => {
        const element = document.getElementById('book');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }

    // Exit intent detection
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShownPopup) {
        setShowExitPopup(true);
        setHasShownPopup(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasShownPopup]);

  const openCalendly = () => {
    window.open(siteConfig.calendlyUrl, '_blank');
  };

  const downloadPDF = () => {
    window.open(PDF_URL, '_blank');
  };

  const closePopup = () => {
    setShowExitPopup(false);
  };

  const symptoms = [
    { icon: Battery, title: "Chronic Fatigue", description: "Persistent exhaustion that doesn't improve with rest" },
    { icon: Brain, title: "Brain Fog", description: "Difficulty concentrating, memory problems, mental confusion" },
    { icon: Frown, title: "Mood Instability", description: "Anxiety, depression, irritability, emotional swings" },
    { icon: Moon, title: "Sleep Disruption", description: "Insomnia, poor sleep quality, waking unrefreshed" },
    { icon: Activity, title: "Chronic Pain", description: "Joint pain, muscle aches, inflammation throughout the body" },
    { icon: Eye, title: "Premature Aging", description: "Skin damage, wrinkles, looking older than your years" },
    { icon: Thermometer, title: "Weak Immunity", description: "Frequent illness, slow healing, increased infections" },
    { icon: Heart, title: "Heart Issues", description: "Irregular heartbeat, high blood pressure, circulation problems" },
  ];

  const longTermEffects = [
    {
      organ: "Brain",
      icon: Brain,
      effects: [
        "Neuronal death and brain shrinkage",
        "Permanent memory impairment",
        "Increased risk of dementia",
        "Persistent anxiety and depression"
      ]
    },
    {
      organ: "Heart",
      icon: Heart,
      effects: [
        "Cardiomyopathy (weakened heart muscle)",
        "Increased heart attack risk",
        "Chronic high blood pressure",
        "Blood vessel damage"
      ]
    },
    {
      organ: "Liver",
      icon: Activity,
      effects: [
        "Fibrosis and scarring",
        "Reduced detoxification ability",
        "Compromised metabolism",
        "Increased toxin buildup"
      ]
    },
    {
      organ: "Immune System",
      icon: Shield,
      effects: [
        "Suppressed immune function",
        "Increased cancer risk",
        "Chronic inflammation",
        "Slow wound healing"
      ]
    }
  ];

  const faqItems = [
    {
      question: "What exactly is oxidative stress?",
      answer: "Oxidative stress occurs when there's an imbalance between free radicals (unstable molecules) and antioxidants in your body. Free radicals damage cells, proteins, and DNA. Substance abuse dramatically increases free radical production while depleting your body's natural antioxidants, creating a destructive cycle that damages every organ system."
    },
    {
      question: "Can oxidative damage be reversed?",
      answer: "Yes, much of the damage can be addressed. The body has remarkable healing capacity when given the right support. Hydrogen water works at the cellular level to neutralize harmful free radicals, reduce inflammation, and support the body's natural repair mechanisms. Many people see significant improvements in energy, clarity, and overall wellness."
    },
    {
      question: "How is hydrogen water different from regular water?",
      answer: "Molecular hydrogen (H2) is the smallest molecule in existence. Unlike regular antioxidants that can't penetrate cells effectively, hydrogen molecules easily cross cell membranes and reach the mitochondria—the powerhouses of your cells. This allows it to neutralize free radicals at the source of energy production."
    },
    {
      question: "Do I need medical training to help others with this?",
      answer: "No medical credentials are required. As a Recovery Assistance Coach, you'll be providing hydrogen-rich water as a wellness support—not medical treatment. We provide comprehensive training on the science, how to educate clients, and how to build your business ethically and effectively."
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section - Matching Page 1 vibe */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Background matching page 1 */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1682985045797-0b781d0c0f7a?w=1920&q=80')`,
            backgroundPosition: 'center 30%',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/80 via-stone-900/70 to-stone-900/90" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/20 backdrop-blur-sm rounded-full mb-6 border border-sky-400/30">
            <AlertTriangle className="w-4 h-4 text-sky-300" />
            <span className="text-sky-200 text-sm font-medium">Hidden Danger of Addiction</span>
          </div>
          
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
            Residual Oxidative Stress<br />
            <span className="text-sky-300">& Cellular Damage</span>
          </h1>
          
          <p className="text-xl text-stone-300 max-w-3xl mx-auto mb-8">
            Even after recovery begins, invisible damage persists at the cellular level. Understanding this is the first step to helping others truly heal.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={openCalendly}
              className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-4 h-auto text-lg font-medium rounded-xl"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book a Q&A With a Mentor
            </Button>
            
            <Button 
              onClick={downloadPDF}
              className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 h-auto text-lg font-medium rounded-xl"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Your Printable PDF Here
            </Button>
          </div>
        </div>
      </section>

      {/* What is Oxidative Stress */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-6">
                What Happens Inside The Body
              </h2>
              
              <p className="text-lg text-stone-600 mb-6">
                Every time someone uses drugs or alcohol, their body produces an excess of <strong>free radicals</strong>—unstable molecules that attack and damage healthy cells.
              </p>
              
              <p className="text-stone-600 mb-6">
                Normally, the body uses antioxidants to neutralize these free radicals. But substance abuse creates a devastating imbalance:
              </p>
              
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap className="w-3.5 h-3.5 text-sky-600" />
                  </div>
                  <span className="text-stone-700"><strong>Massive increase</strong> in free radical production</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap className="w-3.5 h-3.5 text-sky-600" />
                  </div>
                  <span className="text-stone-700"><strong>Severe depletion</strong> of natural antioxidants</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap className="w-3.5 h-3.5 text-sky-600" />
                  </div>
                  <span className="text-stone-700"><strong>Chronic inflammation</strong> throughout the body</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap className="w-3.5 h-3.5 text-sky-600" />
                  </div>
                  <span className="text-stone-700"><strong>Cumulative damage</strong> to cells, proteins, and DNA</span>
                </li>
              </ul>
              
              <p className="text-stone-600">
                This is <strong>oxidative stress</strong>—and it continues affecting the brain, heart, liver, kidneys, and every organ system even after someone stops using.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-sky-50 to-slate-100 rounded-2xl p-8 border border-sky-100">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-sky-600" />
                </div>
                <h3 className="font-display text-2xl font-bold text-stone-900">The Vicious Cycle</h3>
              </div>
              <div className="space-y-3">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-sky-100">
                  <p className="text-stone-700 text-center">Substance Use</p>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-sky-500 rotate-90" />
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-sky-100">
                  <p className="text-stone-700 text-center">Free Radical and Oxidative Stress</p>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-sky-500 rotate-90" />
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-sky-100">
                  <p className="text-stone-700 text-center">Silent Cellular Damage</p>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-sky-500 rotate-90" />
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-sky-100">
                  <p className="text-stone-700 text-center">Recovery</p>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-sky-500 rotate-90" />
                </div>
                <div className="bg-sky-100 rounded-xl p-4 border border-sky-200">
                  <p className="text-sky-800 text-center font-medium">Oxidative Symptoms and Illness</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              Signs of Oxidative Damage
            </h2>
            <p className="text-xl text-stone-600 max-w-2xl mx-auto">
              People in recovery often experience these symptoms without understanding why
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {symptoms.map((symptom, index) => {
              const IconComponent = symptom.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-lg hover:border-sky-200 transition-all"
                >
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-sky-600" />
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-2">{symptom.title}</h3>
                  <p className="text-stone-600 text-sm">{symptom.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Long Term Effects - The New Chapter at Risk */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 rounded-full mb-4">
              <Clock className="w-4 h-4 text-sky-600" />
              <span className="text-sky-800 text-sm font-medium">Protecting The New Chapter</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              Why Longevity Matters in Recovery
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              After building a new chapter in life, we want to give people the best support for longevity. Oxidative-related diseases can affect or even shorten that new chapter.
            </p>
          </div>
          
          {/* Warning callout */}
          <div className="mb-12 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-10 h-10 text-sky-400 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-xl mb-3">The Hidden Threat to Recovery</h3>
                <p className="text-stone-300 text-lg leading-relaxed">
                  Oxidative stress doesn't just cause discomfort—it increases the risk of serious diseases like <strong className="text-white">cancer</strong>, <strong className="text-white">cardiovascular disease</strong>, <strong className="text-white">diabetes</strong>, and <strong className="text-white">neurodegenerative conditions</strong>. For someone who has worked hard to build a new life, these risks can threaten everything they've achieved.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {longTermEffects.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div 
                  key={index}
                  className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200"
                >
                  <h3 className="font-display text-xl font-bold text-stone-900 mb-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-sky-600" />
                    </div>
                    {item.organ}
                  </h3>
                  <ul className="space-y-3">
                    {item.effects.map((effect, i) => (
                      <li key={i} className="flex items-start gap-3 text-stone-700">
                        <div className="w-1.5 h-1.5 bg-sky-500 rounded-full mt-2 flex-shrink-0" />
                        {effect}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE SOLUTION - Large & Prominent */}
      <section id="solution" className="py-20 md:py-32 bg-gradient-to-br from-sky-600 via-sky-700 to-slate-800 text-white relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-400/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
              <Droplets className="w-5 h-5 text-sky-200" />
              <span className="text-sky-100 font-medium">The Solution</span>
            </div>
            
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Hydrogen Water:<br />
              <span className="text-sky-200">Cellular Recovery</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-sky-100 max-w-3xl mx-auto mb-8">
              Through the consumption of hydrogen water, we can neutralize much of the oxidative damage done and support the body's natural healing.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
              <div className="w-20 h-20 bg-sky-400/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Zap className="w-10 h-10 text-sky-200" />
              </div>
              <h3 className="font-semibold text-xl mb-3">Neutralizes Free Radicals</h3>
              <p className="text-sky-200">Molecular hydrogen selectively targets harmful free radicals at the cellular level</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
              <div className="w-20 h-20 bg-sky-400/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-sky-200" />
              </div>
              <h3 className="font-semibold text-xl mb-3">Reduces Inflammation</h3>
              <p className="text-sky-200">Calms chronic inflammatory responses that perpetuate cellular damage</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
              <div className="w-20 h-20 bg-sky-400/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-sky-200" />
              </div>
              <h3 className="font-semibold text-xl mb-3">Supports Longevity</h3>
              <p className="text-sky-200">Helps protect the new chapter they've built for a longer, healthier life</p>
            </div>
          </div>
          
          {/* Mission statement */}
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20 text-center">
            <h3 className="font-display text-2xl md:text-3xl font-bold mb-4">Our Mission</h3>
            <p className="text-xl text-sky-100 leading-relaxed max-w-3xl mx-auto">
              We help people in recovery by giving them the water and the support to fully detox at the cellular level—so they can live longer, healthier lives and truly enjoy the new chapter they've worked so hard to build.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              Common Questions
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-xl border-none px-6 shadow-sm"
              >
                <AccordionTrigger className="text-left font-semibold text-stone-900 hover:no-underline py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-stone-600 pb-5 leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Download PDF Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-amber-50 to-stone-100 rounded-2xl p-8 md:p-12 border border-amber-200">
            <Download className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <h2 className="font-display text-2xl md:text-3xl font-bold text-stone-900 mb-4">
              Free Guide: Cellular Recovery After Substance Use
            </h2>
            <p className="text-stone-600 mb-6 max-w-xl mx-auto">
              Learn the science behind oxidative stress and how hydrogen water supports cellular repair in recovery.
            </p>
            <Button 
              onClick={downloadPDF}
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 h-auto text-lg font-medium rounded-xl"
            >
              <Download className="w-5 h-5 mr-2" />
              Learn More - Get The PDF Here
            </Button>
          </div>
        </div>
      </section>

      {/* Book CTA - Primary Call to Action */}
      <section id="book" className="py-16 md:py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-stone-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Ready To Make a Difference?
          </h2>
          
          <p className="text-xl text-stone-300 mb-8 max-w-2xl mx-auto">
            Walk with others through recovery. Help them heal at the cellular level while building a meaningful business.
          </p>
          
          <div className="bg-sky-900/40 backdrop-blur-sm rounded-2xl p-8 max-w-md mx-auto border border-white/10">
            <div className="space-y-3 text-left mb-6">
              {[
                'Talk with an experienced mentor',
                'Learn about training and support',
                'Get your questions answered',
                'See if this path is right for you'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-white">
                  <CheckCircle2 className="w-5 h-5 text-sky-400 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <Button 
              onClick={openCalendly}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 h-auto text-lg font-medium rounded-xl"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book a Q&A With a Mentor
            </Button>
          </div>
        </div>
      </section>

      {/* Exit Intent Popup */}
      {showExitPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closePopup}
          />
          
          {/* Popup */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in-up">
            <button 
              onClick={closePopup}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-amber-600" />
              </div>
              
              <h3 className="font-display text-2xl font-bold text-stone-900 mb-3">
                Wait! Don't Leave Empty-Handed
              </h3>
              
              <p className="text-stone-600 mb-6">
                Download our free guide on <strong>Cellular Recovery After Substance Use</strong> and learn how to support deeper healing.
              </p>
              
              <Button 
                onClick={() => {
                  downloadPDF();
                  closePopup();
                }}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white px-6 py-4 h-auto text-base font-medium rounded-xl mb-3"
              >
                <Download className="w-5 h-5 mr-2" />
                Get the Free PDF
              </Button>
              
              <button 
                onClick={closePopup}
                className="text-stone-400 hover:text-stone-600 text-sm transition-colors"
              >
                No thanks, I'll pass
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityPage;
